import { Col, Container, Row } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import NavigationButton from './buttons/NavigationButton'
import { useStore } from '../../app/stores/store'
import { AuctionFilter, FilterModes } from '../../app/models/modes'
import { history } from '../..'
import { NavigationFilter } from '../../app/models/Filter'
import { AuctionStatus } from '../../app/models/Auction'
import { PostStatus } from '../../app/models/Post'

interface Props {
  type: FilterModes
  auctionStatus?: AuctionStatus
  postStatus?: PostStatus
}

function Navigation({ type, auctionStatus, postStatus }: Props) {
  const { navigationStore, methodsStore, postListStore, auctionListStore } = useStore()
  const { filterAuctionList, filterPostList } = navigationStore
  const initialState = {
    city: '',
    priceRange: '',
    rooms: '',
    sortBy: AuctionFilter.active,
  }
  const [navigationFilter, setNavigationFilter] = useState<NavigationFilter>(initialState)
  const [search, setSearch] = useState<any>()
  const location = useLocation()
  const isAuctionList = location.pathname.includes('/auctions')
  const isAuctionDetails = location.pathname.includes('/auctions/')
  const isPostList = location.pathname.includes('/posts')
  const isPostDetails = location.pathname.includes('/posts/')
  const { t } = useTranslation()
  const queryParams = new URLSearchParams(location.search)
  const queryFilter: any = {
    city: queryParams.get('city'),
    priceRange: queryParams.get('priceRange'),
    rooms: queryParams.get('rooms'),
    sortBy: queryParams.get('sortBy'),
  }

  useEffect(() => {
    if (queryParams.toString()) {
      setNavigationFilter(queryFilter)
      if (isPostList) {
        filterPostList(queryFilter)
      }
      if (isAuctionList) {
        filterAuctionList(queryFilter)
      }
    }
  }, [postListStore.originalPosts, auctionListStore.originalAuctions])

  useEffect(() => {
    const handleBeforeUnload = () => localStorage.setItem('refreshing', 'true')
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  useEffect(() => {
    const isRefreshing = localStorage.getItem('refreshing')
    if (isRefreshing === 'true') {
      localStorage.removeItem('refreshing')
      history.replace(location.pathname)
      setNavigationFilter(initialState)
    }
  }, [history, location])

  const onLoadPlaces = (e: any) => {
    setSearch(e)
  }

  const onPlacesChanged = () => {
    const google = search.getPlace()
    const components = google.address_components
    const cityComponent = components.find((c: any) => c.types.includes('locality'))

    setNavigationFilter({ ...navigationFilter, city: cityComponent.long_name })
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target
    setNavigationFilter({ ...navigationFilter, [name]: value })
  }

  const handleInputChangeNumber = (e: any) => {
    const { name, value } = e.target
    setNavigationFilter({ ...navigationFilter, [name]: parseInt(value) })
  }

  const handleBtnSubmit = () => {
    if (type === FilterModes.auction) {
      filterAuctionList(navigationFilter)
    } else {
      filterPostList(navigationFilter)
    }
    const queryParams = new URLSearchParams(Object.entries(navigationFilter)).toString()
    if (isAuctionDetails) {
      history.push(`/auctions?${queryParams}`)
    }
    if (isPostDetails) {
      history.push(`/posts?${queryParams}`)
    }
  }

  const navigationLinks = [
    { title: t('home'), link: '/', show: true },
    {
      title: t('auction_list'),
      link: '/auctions',
      show: isAuctionList || isAuctionDetails,
    },
    {
      title: t('auction_details'),
      show: isAuctionDetails,
    },
    {
      title: t('post_list'),
      link: '/posts',
      show: isPostList || isPostDetails,
    },
    {
      title: t('post_details'),
      show: isPostDetails,
    },
  ]

  const navigationButtons = [
    {
      md: isAuctionList || isAuctionDetails ? 3 : 4,
      title: `${t('where')}, ${t('city')}`,
      buttonTitle: t('enter_location'),
      icon: 'chevron-down',
      type: 'input',
      onLoadPlaces,
      onPlacesChanged,
      name: 'city',
      value: navigationFilter.city,
      handleChange: handleInputChange,
    },
    {
      md: 3,
      title: t('price_range'),
      buttonTitle: t('select'),
      icon: 'chevron-down',
      type: 'select',
      handleChange: handleInputChange,
      name: 'priceRange',
      dropdownMenu: [
        '0-50,000',
        '50,000-100,000',
        '100,000-200,000',
        '200,000-500,000',
        '500,000-1,000,000',
        '> 1,000,000',
      ],
      value: navigationFilter.priceRange,
    },
    {
      md: isAuctionList || isAuctionDetails ? 2 : 3,
      title: t('room'),
      buttonTitle: t('select'),
      icon: 'chevron-down',
      type: 'select',
      handleChange: handleInputChange,
      name: 'rooms',
      dropdownMenu: ['1-2', '2-4', '4-6', '6-10', '10-20', '> 20'],
      value: navigationFilter.rooms,
    },
    {
      md: 2,
      title: 'Sort By',
      icon: 'chevron-down',
      type: 'select',
      handleChange: handleInputChangeNumber,
      name: 'sortBy',
      dropdownMenu: [
        { name: t('upcoming'), value: AuctionFilter.upcoming },
        { name: t('active'), value: AuctionFilter.active },
        { name: t('finished'), value: AuctionFilter.finished },
      ],
      value: navigationFilter.sortBy,
      show: !(isAuctionList || isAuctionDetails),
    },
    {
      md: 2,
      buttonTitle: t('filter'),
      icon: 'sliders',
      type: 'button',
      handleSubmit: handleBtnSubmit,
    },
  ]

  const renderBtns = [
    {
      title: t('edit_auction'),
      icon: 'fa-solid fa-pen',
      onClick: () => methodsStore.openShowEditAuction(),
      show:
        isAuctionDetails &&
        methodsStore.isAuctionCreator() &&
        !methodsStore.isAuctionFinished() &&
        auctionStatus !== AuctionStatus.Deleted,
    },
    {
      title: t('delete_auction'),
      icon: 'fa-solid fa-trash-can',
      onClick: () => methodsStore.setShowDelete(true),
      show:
        isAuctionDetails &&
        methodsStore.isAuctionCreator() &&
        auctionStatus !== AuctionStatus.Deleted,
    },
    {
      title: t('edit_post'),
      icon: 'fa-solid fa-pen',
      onClick: () => methodsStore.openShowEditPost(),
      show: isPostDetails && methodsStore.isPostCreator() && postStatus !== PostStatus.Deleted,
    },
    {
      title: t('delete_post'),
      icon: 'fa-solid fa-trash-can',
      onClick: () => methodsStore.setShowDelete(true),
      show: isPostDetails && methodsStore.isPostCreator() && postStatus !== PostStatus.Deleted,
    },
  ]

  return (
    <Container fluid className='navigation-container'>
      <div className='navigation'>
        <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
          <div className='navigation-links'>
            {navigationLinks.map(
              (nav, index) =>
                nav.show && (
                  <p
                    key={index}
                    onClick={() => {
                      if (index !== navigationLinks.length - 1) {
                        history.push(nav.link || '')
                      }
                    }}
                    className='navigation-link'
                  >
                    <span>{nav.title}</span>
                  </p>
                ),
            )}
          </div>
          <div className='d-flex gap-2 flex-wrap justify-content-start'>
            {renderBtns.map(
              (link, index) =>
                link.show && (
                  <button key={index} className='add-auction-btn' onClick={link.onClick}>
                    <i className={link.icon} /> {link.title}
                  </button>
                ),
            )}
          </div>
        </div>

        <Container fluid className='navigation-filters'>
          <Row className='align-items-end mb-2'>
            {navigationButtons.map(
              (button, index) =>
                !button.show && (
                  <Col key={index} md={button.md} className='filter-buttons mb-3 mb-md-0'>
                    <NavigationButton
                      title={button.title}
                      buttonTitle={button.buttonTitle}
                      icon={button.icon}
                      type={button.type}
                      dropdownMenu={button.dropdownMenu}
                      name={button.name}
                      handleChange={button.handleChange}
                      handleSubmit={button.handleSubmit}
                      onLoadPlaces={button.onLoadPlaces}
                      onPlacesChanged={button.onPlacesChanged}
                      value={button.value}
                    />
                  </Col>
                ),
            )}
          </Row>
        </Container>
      </div>
    </Container>
  )
}
export default observer(Navigation)
